import { useForm } from "react-hook-form";
import { CreateMedicationFormModel } from "../types/CreateMedicationFormModel";
import {
  useCreateMedicationMutation,
  useDiscontinueMedicationMutation,
  useUpdateMedicationMutation,
} from "app/services/api/medicationsApi";
import { MedicationModel } from "app/services/api/types/medications/models/MedicationModel";
import { useResidentContext } from "app/features/residents/provider/ResidentProvider";
import { FrequencyEnum } from "app/services/api/types/medications/enums/FrequencyEnum";
import { CreateMedicationRequest } from "app/services/api/types/medications/requests/CreateMedicationRequest";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import { createDateStringFromInput } from "ui/utils/dateUtils";
import { useActionSignatureContext } from "app/features/actionSignature/providers/ActionSignatureProvider";
import { BackendErrorResponse } from "app/services/api/types/common/models/ErrorResponse";

interface Props {
  initialMedication?: MedicationModel;
  onUpdateSuccess?: (newMedicationId: number) => void;
  onCreateSuccess?: () => void;
  onDeleteSuccess?: () => void;
}
export function useHandleMedication({
  initialMedication,
  onCreateSuccess,
  onUpdateSuccess,
  onDeleteSuccess,
}: Props) {
  const { residentId } = useResidentContext();
  const { showActionSignatureModal } = useActionSignatureContext();
  const [triggerCreate] = useCreateMedicationMutation();
  const [triggerUpdate] = useUpdateMedicationMutation();
  const [triggerDiscontinue, { isLoading: isDeleting }] =
    useDiscontinueMedicationMutation();

  const { showNewToast, showErrorToast, showOfflineToast } = useDisplayToast();
  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<CreateMedicationFormModel>({
    defaultValues: {
      name: initialMedication?.name ?? "",
      resident_id: residentId ?? initialMedication?.resident_id ?? undefined,
      diagnosis: initialMedication?.diagnosis ?? "",
      dosage: initialMedication?.dosage ?? "",
      form: initialMedication?.form ?? undefined,
      administration_method:
        initialMedication?.administration_method ?? undefined,
      frequency: initialMedication?.frequency ?? undefined,
      // times: initialMedication?.times ?? undefined,
      start_date: initialMedication?.start_date ?? "",
      end_date: initialMedication?.end_date ?? "",
      is_prn: initialMedication?.is_prn ?? false, // ? do we need?
      is_self_admin: initialMedication?.is_self_admin ?? false,
      notes: initialMedication?.notes ?? "",
      prescribed_by: initialMedication?.prescribed_by ?? "",
      day_of_week: initialMedication?.day_of_week,
      day_of_month: initialMedication?.day_of_month,
      time_of_day: initialMedication?.time_of_day ?? undefined,
    },
  });

  const submit = handleSubmit(async (data) => {
    try {
      const resp = await showActionSignatureModal({
        title: "Create Medication",
        description: "Please enter your password to create this medication",
      });
      const transformedData: CreateMedicationRequest = {
        ...data,
        is_prn: data.frequency === FrequencyEnum.PRN,
        start_date: createDateStringFromInput(data.start_date),
        end_date: data.end_date
          ? createDateStringFromInput(data.end_date)
          : undefined,
        password: resp.password,
      };

      let newId: number;
      if (initialMedication) {
        const updatedMedication = await triggerUpdate({
          body: transformedData,
          id: initialMedication.id,
        }).unwrap();
        newId = updatedMedication?.id;

        showNewToast({
          title: "Medication updated",
          message: "Successfully updated medication",
          type: "success",
        });

        if (onUpdateSuccess) onUpdateSuccess(newId);
      } else {
        await triggerCreate(transformedData).unwrap();

        showNewToast({
          title: "Medication created",
          message: "Successfully created medication",
          type: "success",
        });
        if (onCreateSuccess) onCreateSuccess();
      }
    } catch (e) {
      // Signature modal cancellation rejects with no argument — not an error.
      if (e === undefined) return;

      // Surface backend field-level validation (Laravel 422) on the form so the
      // user can see exactly which field was rejected (e.g. start_date).
      const backendError = e as Partial<BackendErrorResponse>;
      const fieldErrors = backendError?.data?.errors;
      if (fieldErrors) {
        Object.entries(fieldErrors).forEach(([field, messages]) => {
          if (messages?.length) {
            setError(field as keyof CreateMedicationFormModel, {
              type: "server",
              message: messages[0],
            });
          }
        });
      }

      showOfflineToast(
        {
          title: "Form saved offline",
        },
        () => showErrorToast(e, { title: "Could not save medication" }),
      );
    }
  });

  const handleDeleteMedication = async ({
    id,
  }: Pick<MedicationModel, "id">) => {
    try {
      if (!residentId) return;

      const password = await showActionSignatureModal({
        title: "Delete Medication",
        description:
          "Please enter your password to discontinue this medication",
      });
      await triggerDiscontinue({
        medication: id,
        residentId,
        body: password,
      });
      showNewToast({
        title: "Medication deleted successfully",
        message: "The medication has been deleted successfully",
        type: "success",
      });
      if (onDeleteSuccess) onDeleteSuccess();
    } catch (e) {
      showErrorToast(e);
    }
  };

  return {
    control,
    submit,
    handleDeleteMedication,
    isLoadingAction: isDeleting || isSubmitting,
  };
}
